import type { PageHelp } from '../network-help.data';

export const L10N_fr_g1: Record<string, PageHelp> = {
  customers: {
    title: 'Clients',
    tag: 'Répertoire CRM',
    tagline: 'La fiche maîtresse des voyageurs et des entreprises auxquels une agence vend.',
    business: {
      lead: 'Les clients sont ceux à qui l’agence vend réellement — voyageurs loisirs, comptes entreprises et compagnons qui voyagent avec eux. Conserver une fiche propre par client est ce qui permet à la plateforme de préremplir les réservations, de tarifer un segment différemment, d’animer un club de fidélité et de facturer la bonne partie. Un client facturable devient aussi un contact comptable (طرف حساب) en aval.',
      points: [
        { title: 'Une source unique de vérité', desc: 'Le nom, le document, le contact et le consentement figurent sur la fiche client — les écrans de réservation la lisent, donc un voyageur est saisi une fois et réutilisé partout.' },
        { title: 'Les segments pilotent le prix', desc: 'Un client peut appartenir à un groupe de clients ; la couche Retail de Markup & Rules peut alors tarifer ce groupe différemment — une remise pour les VIP, des frais pour les clients de passage.' },
        { title: 'Le consentement est enregistré', desc: 'L’opt-in marketing et le consentement au traitement des données sont stockés par client afin que la prospection reste conforme.' },
      ],
    },
    software: {
      lead: 'Une liste recherchable de chaque client, avec les colonnes de contact, de fidélité et de consentement dont vous avez besoin pour les gérer.',
      points: [
        { title: 'Trouver & filtrer', desc: 'Recherchez par nom, téléphone, e-mail ou numéro national ; ouvrez une ligne pour le profil complet.' },
        { title: 'Modifier le profil', desc: 'Le panneau d’édition capture l’identité, le téléphone/WhatsApp (indicatif + numéro séparés), la nationalité, le canal et la langue préférés, le niveau VIP et les notes internes ; ajoutez les compagnons qui voyagent avec eux.' },
        { title: 'La fidélité en un coup d’œil', desc: 'Chaque profil affiche le solde de points du client, son niveau et son historique, avec gain / échange / ajustement manuels.' },
      ],
    },
    related: ['customer-groups', 'loyalty', 'markup', 'agencies'],
  },

  'customer-groups': {
    title: 'Groupes de clients',
    tag: 'Segmentation',
    tagline: 'Étiquetez les clients en segments pour que la couche de tarification Retail les traite différemment.',
    business: {
      lead: 'Un groupe de clients est un segment nommé — « VIP », « Entreprise — Acme », « Clients de passage » — que vous pouvez tarifer. C’est l’équivalent côté client d’un groupe d’agences : là où les groupes d’agences cadrent les règles Wholesale/Distribution, les groupes de clients cadrent la couche Retail, afin que l’agence vendeuse puisse récompenser ou surtaxer une partie de ses propres clients.',
      points: [
        { title: 'Un levier de marge retail', desc: 'L’appartenance à un groupe est ce que cible une règle Retail — par ex. une remise de fidélité de −3 % pour le groupe VIP, ou des frais de service pour les clients de passage non vérifiés.' },
        { title: 'Appartenance, pas hiérarchie', desc: 'Un client appartient simplement à un groupe ; il n’y a pas d’imbrication. Une étiquette claire rend la tarification prévisible.' },
        { title: 'Détenu par l’agence', desc: 'Chaque agence définit ses propres groupes sur ses propres clients — ils ne fuient pas à travers le réseau.' },
      ],
    },
    software: {
      lead: 'Créez des groupes et affectez des clients, puis référencez le groupe depuis la portée Group d’une règle de tarification Retail.',
      points: [
        { title: 'Créer & nommer un groupe', desc: 'Définissez un groupe avec un code et un nom ; activez-le ou archivez-le à mesure que les segments évoluent.' },
        { title: 'Affecter des membres', desc: 'Ajoutez ou retirez des clients d’un groupe ; le décompte apparaît sur le groupe et sur les lignes à portée Group de Markup & Rules.' },
        { title: 'Le câbler à la tarification', desc: 'Dans Markup & Rules, choisissez partner-scope = Group sur une règle Retail et sélectionnez ce groupe — chaque réservation correspondante pour ses membres applique la règle.' },
      ],
    },
    examples: [
      {
        title: 'Une remise pour le groupe VIP sur la couche Retail',
        rows: [
          { label: 'Sous-total voyageur (avant règle retail)', value: '11,024,000' },
          { label: 'DISCOUNT Retail −3 % · groupe = VIP', value: '−330,720', accent: 'success', strong: true },
          { label: 'Le client VIP paie', value: '10,693,280' },
        ],
        note: 'La même réservation pour un client non-VIP ignore la règle et paie 11,024,000. L’appartenance au groupe est la seule chose qui diffère.',
      },
      {
        title: 'Des frais de service pour un client de passage — l’autre sens',
        rows: [
          { label: 'Sous-total voyageur', value: '11,024,000' },
          { label: 'SERVICE_FEE Retail +150,000 · groupe = Clients de passage', value: '+150,000', accent: 'accent', strong: true },
          { label: 'Le client de passage paie', value: '11,174,000' },
        ],
        note: 'Un SERVICE_FEE forfaitaire surtaxe un segment non vérifié. Les règles forfaitaires ignorent le montant du tarif — chaque réservation de passage ajoute les mêmes 150,000, quel que soit le prix du billet.',
      },
      {
        title: 'Tarif net entreprise — un groupe peut aussi débloquer un accord négocié',
        rows: [
          { label: 'Sous-total voyageur standard', value: '11,024,000' },
          { label: 'DISCOUNT Retail −6 % · groupe = Entreprise · Acme', value: '−661,440', accent: 'success', strong: true },
          { label: 'L’employé d’Acme paie', value: '10,362,560' },
        ],
        note: 'Une remise de groupe plus forte encode un contrat entreprise : toute personne étiquetée dans « Entreprise · Acme » obtient automatiquement les 6 % négociés d’Acme — sans dérogation par réservation.',
      },
    ],
    related: ['customers', 'markup', 'agency-groups', 'loyalty'],
  },

  loyalty: {
    title: 'Club de fidélité',
    tag: 'Voyager Rewards',
    tagline: 'Le programme de points de l’agence — comment les voyageurs gagnent, montent de niveau et échangent.',
    business: {
      lead: 'Un club de fidélité transforme les voyageurs occasionnels en habitués. Les clients gagnent des points en réservant, montent des niveaux qui débloquent des avantages et de meilleurs taux de gain, et échangent leurs points contre de futures réservations. Le solde en cours est un vrai passif que porte l’agence, donc le club est piloté avec des taux de gain et d’échange explicites.',
      points: [
        { title: 'La rétention, quantifiée', desc: 'Les points et les niveaux donnent au client une raison de réserver à nouveau chez vous plutôt que chez un concurrent.' },
        { title: 'Niveaux par cumul de gains', desc: 'Le niveau d’un client reflète les points gagnés à vie, donc échanger ne le rétrograde jamais — Bronze → Argent → Or → Platine, chacun avec un multiplicateur de gain plus élevé.' },
        { title: 'Les points sont un passif', desc: 'Chaque point non échangé est de l’argent dû ; l’aperçu du club fait apparaître le total des points et le passif en devise pour qu’il reste inscrit aux comptes.' },
      ],
    },
    software: {
      lead: 'Un aperçu du club plus les deux choses qui le pilotent : les paramètres (taux + déclencheur) et les règles de gain automatique.',
      points: [
        { title: 'KPI de l’aperçu', desc: 'Membres, total des points, passif et répartition par niveau — calculés en direct depuis le grand livre.' },
        { title: 'Paramètres du club', desc: 'Définissez le taux de gain, le taux d’échange (points par unité), l’expiration des points et le déclencheur de gain automatique — attribution à l’ISSUE (émis) ou au PAID (paiement encaissé).' },
        { title: 'Règles de gain', desc: 'Rédigez des règles de points par produit (par ex. les Vols gagnent 2× par unité) ; l’achèvement d’une réservation attribue automatiquement les points de la règle correspondante. Le gain / échange / ajustement par client est sur chaque profil.' },
      ],
    },
    modes: {
      lead: 'Deux concepts de niveau distincts existent — ne les confondez pas.',
      modes: [
        { name: 'Niveau de fidélité', when: 'gagné', desc: 'Bronze / Argent / Or / Platine, dérivé des points gagnés à vie. Pilote le multiplicateur de gain et les avantages. Jamais défini à la main.' },
        { name: 'Niveau VIP', when: 'manuel', desc: 'Un indicateur distinct qu’un agent définit sur la fiche client (Standard / Argent / Or / VIP) pour le traitement du service — indépendant des points de fidélité.' },
      ],
    },
    examples: [
      {
        title: 'Seuils de niveau — définis par les points gagnés à vie',
        rows: [
          { label: 'Bronze · 0+ gagnés', value: '1× earn' },
          { label: 'Argent · 2,000+ gagnés', value: '1.25× earn' },
          { label: 'Or · 6,000+ gagnés', value: '1.5× earn', accent: 'info' },
          { label: 'Platine · 15,000+ gagnés', value: '2× earn', accent: 'success' },
        ],
        note: 'Le niveau lit le cumul gagné à vie, donc dépenser des points ne rétrograde jamais un client.',
      },
      {
        title: 'Gain automatique sur une réservation, et un saut de niveau',
        rows: [
          { label: 'Cumul gagné avant (Argent)', value: '5,600' },
          { label: 'Règle Vols · 2 pts par unité × 250', value: '+500', accent: 'info', strong: true },
          { label: 'Cumul gagné après → Or', value: '6,100' },
        ],
        note: 'Les points sont crédités automatiquement quand la réservation atteint le déclencheur de gain du club (ISSUE ou PAID). Franchir 6,000 promeut le client au niveau Or — 1.5× de gain dès lors.',
      },
      {
        title: 'Échanger des points (par défaut 100 pts = 1 unité de remise)',
        rows: [
          { label: 'Solde de points', value: '8,000' },
          { label: 'Échanger 5,000 pts → remise', value: '−50', accent: 'success' },
          { label: 'Solde restant', value: '3,000', strong: true },
        ],
        note: 'Le taux d’échange est défini dans les paramètres du club ; le solde correspond aux points dépensables, distinct du cumul gagné à vie qui fixe le niveau.',
      },
      {
        title: 'Passif du club — ce que valent les points en cours',
        rows: [
          { label: 'Total des points non échangés sur les membres', value: '2,400,000' },
          { label: '÷ taux d’échange (100 pts = 1)', value: '÷ 100' },
          { label: 'Passif en devise aux comptes', value: '24,000', accent: 'accent', strong: true },
        ],
        note: 'L’aperçu fait apparaître cela afin que le club ne soit jamais un coût caché. Relever le taux d’échange (plus de points par unité) réduit le passif mais fait gagner moins avec les points pour le client — le levier à équilibrer.',
      },
    ],
    related: ['customers', 'customer-groups', 'markup'],
  },

  agencies: {
    title: 'Agences',
    tag: 'Répertoire',
    tagline: 'Le répertoire maître des entreprises de voyage B2B qui opèrent sur la plateforme.',
    business: {
      lead: 'Les agences sont les clients B2B de la plateforme — les entreprises de voyage auxquelles vous vendez de l’inventaire. Chacune porte les conditions commerciales et de conformité qui régissent sa manière de négocier : son niveau, son portefeuille ou sa ligne de crédit, et sa situation KYC.',
      points: [
        { title: 'Les niveaux fixent les conditions', desc: 'Le niveau d’une agence est le levier derrière sa tarification — il alimente les règles à portée Tier dans le moteur Markup & Rules, afin que le volume puisse être récompensé par une meilleure marge.' },
        { title: 'Portefeuille & crédit sont la limite de négoce', desc: 'Une agence réserve sur un portefeuille prépayé ou une ligne de crédit approuvée ; ce solde est ce qui plafonne l’exposition de la plateforme.' },
        { title: 'Le KYC conditionne le négoce', desc: 'Une nouvelle agence n’est activée qu’une fois le KYC approuvé — la colonne de statut montre, par ligne, qui négocie et qui est bloqué.' },
      ],
    },
    software: {
      lead: 'Une liste recherchable de chaque agence, avec les colonnes commerciales et de conformité dont vous avez besoin pour les gérer d’un coup d’œil.',
      points: [
        { title: 'Parcourir & filtrer', desc: 'Recherchez par nom, contact ou code et filtrez par niveau ou statut pour trouver qui compte.' },
        { title: 'Lire les colonnes de santé', desc: 'Le solde du portefeuille, l’utilisation du crédit et le statut KYC apparaissent par ligne.' },
        { title: 'Ouvrir une fiche', desc: 'Cliquez sur une ligne pour ouvrir le profil de l’agence — modifiez ses conditions, SIGNs, canaux de vente, rôles et accès.' },
      ],
    },
    related: ['agency-profile', 'agents', 'agency-onboarding', 'markup'],
  },

  markup: {
    title: 'Markup & Rules',
    tag: 'Moteur de tarification',
    tagline: 'Là où chaque couche de marge — du propriétaire du SIGN au revendeur puis au voyageur — est définie.',
    business: {
      lead: 'C’est ainsi qu’un prix est construit à partir du tarif net d’un fournisseur jusqu’à ce que paie le voyageur. Le moteur est en couches : un markup Wholesale (le propriétaire du SIGN facture un revendeur), des frais de Distribution (le propriétaire d’un canal facture l’agence qui vend via son canal) et une marge Retail (le propre markup de l’agence vendeuse envers le client). Chaque couche est l’argent d’une partie différente.',
      points: [
        { title: 'Trois couches, trois relations', desc: 'Wholesale = propriétaire du SIGN → revendeur ; Distribution = propriétaire du canal → vendeur ; Retail = vendeur → voyageur. Une règle appartient toujours à exactement une couche.' },
        { title: 'Ciblée par segment', desc: 'Les règles se restreignent par produit, fournisseur, compagnie aérienne, route, cabine, SIGN ou canal, niveau ou groupe de partenaire, ou une agence partenaire précise — afin de tarifer avec précision.' },
        { title: 'Des garde-fous protègent le prix', desc: 'Les plafonds min / max, la priorité et un statut actif / inactif empêchent une mauvaise règle de rendre un tarif non compétitif ou non rentable.' },
      ],
    },
    software: {
      lead: 'Un moteur de règles résolu par spécificité — la règle correspondante la plus spécifique l’emporte au sein de chaque couche.',
      points: [
        { title: 'Ordre de spécificité', desc: 'Partenaire → Niveau → SIGN / Canal → Route → Défaut du propriétaire. Le résolveur parcourt cette échelle pour chaque couche d’une réservation.' },
        { title: 'Le scoring est la façon dont le résolveur choisit', desc: 'Chaque dimension de correspondance non-joker qu’une règle définit ajoute un poids fixe à son score. Le gagnant par couche = score le plus élevé. Égalité → la `priority` la plus haute l’emporte ; toujours égalité → la règle mise à jour le plus récemment l’emporte. Le simulateur affiche le score sur chaque carte candidate pour que vous voyiez exactement pourquoi une règle en a battu une autre.' },
        { title: 'Construire & filtrer les règles', desc: 'Rédigez la couche d’une règle, le type d’ajustement, la valeur (pourcentage / forfait / par paliers), la base et la portée ; filtrez la liste croissante par type, produit, portée partenaire ou statut.' },
        { title: 'Simuler avant la mise en ligne', desc: 'Le simulateur intégré tarife une réservation hypothétique et montre exactement quelle règle se déclenche par couche — sans aucun risque pour la tarification en direct.' },
      ],
    },
    examples: [
      {
        title: 'Poids de spécificité — ce que vaut chaque dimension de correspondance',
        rows: [
          { label: 'Partenaire = AGENCY spécifique', value: '100' },
          { label: 'SIGN (wholesale) · Canal (distribution)', value: '90' },
          { label: 'Origine & Destination — aéroport / gare', value: '70' },
          { label: 'GROUP de partenaire', value: '60' },
          { label: 'Origine & Destination — ville', value: '50' },
          { label: 'TIER de partenaire', value: '40' },
          { label: 'Région', value: '35' },
          { label: 'Fournisseur · Compagnie aérienne · Type de tarif', value: '30 chacun' },
          { label: 'Cabine', value: '25' },
          { label: 'Produit', value: '20' },
        ],
        note: 'Le score d’une règle est la somme des poids de chaque dimension non-joker qui a effectivement correspondu à la réservation. Les jokers (vides) ne comptent pas — ils n’excluent simplement pas.',
      },
      {
        title: 'Deux règles s’affrontent — le score le plus élevé l’emporte',
        rows: [
          { label: 'Réservation : vendeur = Nomad (niveau GOLD), produit = vol', value: '' },
          { label: 'Règle A · WHOLESALE · TIER=GOLD + produit=vol', value: '40 + 20 = 60', accent: 'info' },
          { label: 'Règle B · WHOLESALE · AGENCY=Nomad', value: '100', accent: 'success' },
          { label: 'Gagnant', value: 'Règle B', strong: true },
        ],
        note: 'Les deux règles correspondent à cette réservation. Le verrou sur l’agence partenaire de la Règle B marque 100 à lui seul — il bat la combinaison niveau+produit de la Règle A (60). Le partenaire spécifique est la dimension la plus spécifique que connaît le moteur.',
      },
      {
        title: 'Même score — la priorité départage',
        rows: [
          { label: 'Réservation : produit=vol, cabine=BUSINESS, fournisseur=Amadeus', value: '' },
          { label: 'Règle X · produit+cabine (20+25) · priorité=0', value: 'score 45 · pri 0' },
          { label: 'Règle Y · produit+fournisseur (20+30) · priorité=10', value: 'score 50 · pri 10', accent: 'success' },
          { label: 'Gagnant', value: 'Règle Y (score plus élevé)', strong: true },
        ],
        note: 'Y l’emporte au score brut (50 > 45). Si deux règles étaient à égalité de score, la `priority` départage — et si la `priority` est aussi à égalité, la règle mise à jour le plus récemment l’emporte. Ce dernier départage signifie que modifier une règle existante la rafraîchit effectivement en tête d’un groupe autrement à égalité.',
      },
      {
        title: 'Wholesale — propriétaire du SIGN → revendeur (sur le tarif net)',
        rows: [
          { label: 'Net compagnie aérienne', value: '10,000,000' },
          { label: 'MARKUP +4 % du net', value: '+400,000', accent: 'info' },
          { label: 'Coût du revendeur', value: '10,400,000', strong: true },
        ],
      },
      {
        title: 'Retail — vendeur → voyageur (sur le brut courant)',
        rows: [
          { label: 'Total courant', value: '10,400,000' },
          { label: 'MARKUP +6 % du brut', value: '+624,000', accent: 'success' },
          { label: 'Sous-total voyageur', value: '11,024,000', strong: true },
        ],
      },
      {
        title: 'Distribution — frais de canal (add-on vs partage de revenus)',
        rows: [
          { label: 'Add-on +2 % sur 11,024,000', value: '+220,480', accent: 'accent' },
          { label: 'Partage de revenus 2 % (sur la marge)', value: 'prix inchangé' },
        ],
        note: 'L’add-on est facturé en plus du tarif ; le partage de revenus laisse le prix voyageur intact et déduit la part du canal de la marge du vendeur.',
      },
      {
        title: 'Modes de valeur — base 10,000,000, réservation de 3 pax / 2 segments',
        rows: [
          { label: 'Pourcentage · 5 % × 10,000,000', value: '500,000' },
          { label: 'Forfait · 150,000 par réservation', value: '150,000' },
          { label: 'Forfait · 150,000 par pax × 3', value: '450,000' },
          { label: 'Forfait · 150,000 par segment × 2', value: '300,000' },
          { label: 'Pourcentage + forfait · 3 % + 200,000', value: '500,000' },
        ],
        note: 'Toute couche peut utiliser tout mode. Le type d’ajustement fixe le signe : MARKUP / SERVICE_FEE ajoutent ; DISCOUNT / REBATE soustraient.',
      },
      {
        title: 'Cascade de bout en bout — un partenaire émet sous un SIGN, puis revend',
        rows: [
          { label: 'Net fournisseur (compagnie aérienne)', value: '10,000,000' },
          { label: '+ MARKUP Wholesale 4 %', value: '+400,000', accent: 'info' },
          { label: '+ MARKUP Retail 6 %', value: '+624,000', accent: 'success' },
          { label: '+ Add-on Distribution 2 %', value: '+220,480', accent: 'accent' },
          { label: 'Prix voyageur', value: '11,244,480', strong: true },
        ],
        note: 'Exactement ce que calcule le simulateur. Avec un partage de revenus au lieu d’un add-on, le voyageur paie toujours 11,024,000 et les 220,480 du canal proviennent de la marge du vendeur.',
      },
    ],
    related: ['agency-groups', 'sign-access', 'channel-access', 'agencies'],
  },

  'partner-access': {
    title: 'Accès partenaire',
    tag: 'Appariement',
    tagline: 'Comment les agences s’associent pour partager les identifiants d’émission (SIGNs) et les canaux de vente.',
    business: {
      lead: 'Toutes les agences ne détiennent pas les identifiants aériens (SIGNs) ou les canaux de vente dont elles ont besoin pour tout billetter ce qu’elles peuvent vendre. L’accès partenaire permet à deux agences de se connecter et de partager ces actifs — afin que l’une puisse émettre sur le SIGN d’une autre ou vendre via son canal — élargissant ce que les deux peuvent vendre sans qu’aucune n’achète de nouvel inventaire.',
      points: [
        { title: 'Emprunter la portée, garder le contrôle', desc: 'Le propriétaire accorde l’accès et peut le révoquer à tout moment — le partage est une permission, pas un transfert de l’actif.' },
        { title: 'Les deux parties doivent consentir', desc: 'L’appariement et chaque octroi sont un consentement bilatéral : une offre ou une demande que l’autre partie accepte avant qu’un actif ne soit partagé.' },
        { title: 'Deux directions de valeur', desc: 'Vous suivez ce que vous partagez avec des partenaires et ce que vous utilisez d’eux comme deux grands livres distincts.' },
      ],
    },
    software: {
      lead: 'Cet écran gère les appariements et les octrois de SIGN / Canal de vente qui circulent entre eux.',
      points: [
        { title: 'Se connecter par code d’agence', desc: 'Envoyez une demande de partenariat en utilisant le code d’agence unique d’un partenaire ; il l’accepte ou la refuse.' },
        { title: 'Octroyer des SIGNs & canaux de vente', desc: 'Offrez un SIGN (identifiant d’émission aérien) ou un canal de vente à un partenaire, avec une portée et une durée définies ; le bénéficiaire accepte pour l’activer.' },
        { title: 'Gérer les deux grands livres', desc: 'Passez en revue l’accès que vous avez accordé et celui que vous avez reçu, et révoquez-le lorsqu’une relation se termine.' },
      ],
    },
    examples: [
      {
        title: 'Vous partagez un SIGN — vous gagnez le markup wholesale sur chaque billet partenaire',
        rows: [
          { label: 'Le partenaire émet un billet EK · net compagnie', value: '10,000,000' },
          { label: 'Votre markup Wholesale 4 % (il vous le paie)', value: '+400,000', accent: 'info', strong: true },
          { label: 'Coût du billet pour le partenaire', value: '10,400,000' },
        ],
        note: 'Vous ne touchez jamais à la réservation — partager votre SIGN vous rapporte 400,000 par billet que le partenaire émet dessous. Il garde la marge retail qu’il ajoute pour son propre client.',
      },
      {
        title: 'Vous utilisez le SIGN d’un partenaire — payez son wholesale, gardez votre retail',
        rows: [
          { label: 'Vous émettez un billet EK sur son SIGN · net', value: '10,000,000' },
          { label: '+ Son markup Wholesale 4 % → votre coût', value: '10,400,000' },
          { label: '+ Votre markup Retail 6 % à votre client', value: '+624,000', accent: 'success' },
          { label: 'Votre client paie', value: '11,024,000', strong: true },
        ],
        note: 'Emprunter un SIGN vous permet de vendre une compagnie que vous ne détenez pas. Vous payez le markup wholesale du propriétaire et gardez vos propres 624,000 de marge retail.',
      },
      {
        title: 'Vous partagez un canal de vente — partage de revenus vs add-on',
        rows: [
          { label: 'Le partenaire vend un tarif de 10,000,000 via votre canal', value: '10,000,000' },
          { label: 'Partage de revenus 2 % (votre part, sur sa marge)', value: '+200,000', accent: 'accent', strong: true },
          { label: 'Add-on 2 % (facturé en plus → le voyageur paie)', value: '10,200,000' },
        ],
        note: 'Le partage de revenus vous rapporte 200,000 sur la marge du vendeur avec le prix voyageur inchangé ; l’add-on ajoute vos frais au-dessus du tarif. Vous choisissez lequel, par octroi.',
      },
    ],
    related: ['sign-access', 'channel-access', 'signs', 'sale-channels'],
  },

  agents: {
    title: 'Sous-agences',
    tag: 'Réseau aval',
    tagline: 'Les revendeurs qui se placent sous une agence maîtresse et vendent en son nom.',
    business: {
      lead: 'Les sous-agences étendent la distribution sans que la plateforme n’intègre tout le monde directement. Une agence maîtresse les recrute, fixe leurs conditions commerciales et se porte garante de leur négoce — transformant une relation en une chaîne de vente.',
      points: [
        { title: 'De la portée sans surcharge', desc: 'Chaque sous-agence est un nouveau point de vente que la plateforme n’a jamais eu à contractualiser ou financer individuellement.' },
        { title: 'La marge est en couches', desc: 'L’agence maîtresse peut ajouter son propre markup Retail au-dessus du prix de la plateforme avant que la sous-agence ne voie un tarif — cet écart est son affaire.' },
        { title: 'Responsabilité consolidée', desc: 'Portefeuille, crédit et règlement remontent au parent, donc la plateforme règle avec une seule contrepartie.' },
      ],
    },
    software: {
      lead: 'Liste toute personne rattachée à une agence, avec les contrôles qui régissent ce que chacune peut faire.',
      points: [
        { title: 'Configuration par agence', desc: 'Les dérogations de markup, les produits autorisés et les accès sont définis sur chaque sous-agence depuis sa fiche.' },
        { title: 'Totaux consolidés', desc: 'Les réservations et les revenus du réseau aval s’agrègent au parent et au total réseau.' },
        { title: 'Vue admin vs agence', desc: 'Les admins voient tout le réseau ; une agence connectée ne voit que son propre réseau aval.' },
      ],
    },
    related: ['agencies', 'markup', 'partner-access'],
  },

  companies: {
    title: 'Entreprises',
    tag: 'Parent corporate',
    tagline: 'Les entités juridiques qui détiennent des canaux de vente et regroupent des agences sous un même toit.',
    business: {
      lead: 'Une entreprise est le parent corporate au-dessus des agences — l’entité qui peut détenir des canaux de vente et donc percevoir les frais de la couche Distribution. Regrouper des marques ou des agences sous une même entreprise garde la propriété des actifs commerciaux partagés et du règlement au même endroit.',
      points: [
        { title: 'Détient les actifs de distribution', desc: 'Les entreprises détiennent des canaux de vente (pas des SIGNs) ; les règles Distribution d’une entreprise tarifent ce qui circule via ces canaux.' },
        { title: 'Une seule contrepartie juridique', desc: 'Plusieurs agences ou marques peuvent se placer sous une seule entreprise pour la contractualisation et le règlement.' },
        { title: 'Séparation des responsabilités', desc: 'L’émission aérienne (SIGNs) réside chez les agences ; la distribution par canal réside chez l’entreprise.' },
      ],
    },
    software: {
      lead: 'Le répertoire des entreprises et des canaux qu’elles détiennent.',
      points: [
        { title: 'Gérer les fiches d’entreprise', desc: 'Créez et modifiez des entreprises et voyez les agences et canaux rattachés à chacune.' },
        { title: 'Propriété des canaux', desc: 'Les canaux de vente d’une entreprise se gèrent depuis Canaux de vente et se tarifent depuis Markup & Rules (couche Distribution).' },
        { title: 'Portée dans les sélecteurs', desc: 'Les propriétaires d’entreprise apparaissent dans le sélecteur de propriétaire de Markup & Rules comme « canaux uniquement ».' },
      ],
    },
    related: ['sale-channels', 'channel-access', 'agencies'],
  },

  'sale-channels': {
    title: 'Canaux de vente',
    tag: 'Distribution',
    tagline: 'Les vitrines et les canaux par lesquels l’inventaire est vendu.',
    business: {
      lead: 'Un canal de vente est une voie vers le marché — un site web, une application, une intégration API ou un guichet hors ligne — détenu par une agence ou une entreprise. C’est l’actif sur lequel la couche Distribution facture : quiconque détient le canal peut prélever des frais ou un partage de revenus sur ce qui s’y vend.',
      points: [
        { title: 'Un canal est un point de revenu', desc: 'Les ventes circulant via un canal peuvent porter des frais de Distribution ou un partage de revenus pour le propriétaire du canal.' },
        { title: 'Détenir ou emprunter', desc: 'Vous vendez via vos propres canaux, ou via celui d’un partenaire grâce à un octroi d’accès au canal.' },
        { title: 'Façonner le partage', desc: 'Les frais d’un canal peuvent être un add-on (au-dessus du tarif) ou un partage de revenus (sur la marge du vendeur).' },
      ],
    },
    software: {
      lead: 'Le répertoire des canaux de vente avec leur propriétaire et leur forme de règlement.',
      points: [
        { title: 'Créer & configurer', desc: 'Enregistrez un canal, définissez son propriétaire (agence ou entreprise) et son partage de revenus / sa forme par défaut.' },
        { title: 'Octroyer à des partenaires', desc: 'Partagez un canal via Accès au canal pour qu’une agence partenaire puisse vendre dessus.' },
        { title: 'Alimente la tarification', desc: 'Les règles Distribution de Markup & Rules ciblent un canal précis ou l’ensemble.' },
      ],
    },
    related: ['channel-access', 'companies', 'partner-access', 'markup'],
  },

  'channel-access': {
    title: 'Accès au canal',
    tag: 'Octroi de distribution',
    tagline: 'Permettre à une agence partenaire de vendre via votre canal de vente.',
    business: {
      lead: 'L’accès au canal est la façon dont un canal de vente est prêté à une autre agence. Le propriétaire accorde à un partenaire le droit de vendre via son canal — généralement contre un partage de revenus — élargissant la distribution sans céder l’actif. C’est l’octroi granulaire derrière la couche de tarification Distribution.',
      points: [
        { title: 'De la portée, contre un partage de revenus', desc: 'Le bénéficiaire vend via le canal ; le propriétaire garde une part du brut qui y circule.' },
        { title: 'Consentement bilatéral', desc: 'Un octroi est offert et accepté — rien n’est partagé tant que les deux parties ne consentent pas — et peut être révoqué à tout moment.' },
        { title: 'Pilote les règles Distribution', desc: 'Le partage de revenus de l’octroi est le repli hérité ; les règles Distribution explicites de Markup & Rules le remplacent.' },
      ],
    },
    software: {
      lead: 'Le grand livre des octrois de canal — donnés et reçus.',
      points: [
        { title: 'Octroyer & cadrer', desc: 'Offrez un canal à un partenaire avec un partage de revenus, une forme et une fenêtre de validité optionnelle.' },
        { title: 'Accepter / refuser', desc: 'Le bénéficiaire accepte pour activer l’accès, ou le refuse.' },
        { title: 'Révoquer', desc: 'Mettez fin à l’accès lorsqu’une relation se termine ; les réservations en cours se règlent selon les conditions en vigueur.' },
      ],
    },
    related: ['sale-channels', 'partner-access', 'markup', 'companies'],
  },

  'sign-access': {
    title: 'Accès SIGN',
    tag: 'Octroi wholesale',
    tagline: 'Permettre à une agence partenaire d’émettre des billets sous votre SIGN.',
    business: {
      lead: 'L’accès SIGN prête un identifiant d’émission (un SIGN) à une autre agence afin qu’elle puisse billetter un fournisseur pour lequel elle ne détient pas d’identifiants. Le propriétaire du SIGN accorde l’accès, fixe un markup envers ce bénéficiaire, et peut le révoquer — c’est l’octroi derrière la couche de tarification Wholesale.',
      points: [
        { title: 'Émettre sur des identifiants empruntés', desc: 'Le bénéficiaire émet des billets sous le SIGN du propriétaire sans enregistrer son propre identifiant BSP/IATA.' },
        { title: 'Markup envers le bénéficiaire', desc: 'Le propriétaire fixe un markup wholesale par octroi — sa marge pour prêter le SIGN.' },
        { title: 'Consentement bilatéral', desc: 'Un octroi est offert et accepté, et peut être révoqué ; le partage est une permission, pas un transfert.' },
      ],
    },
    software: {
      lead: 'Le grand livre des octrois de SIGN — donnés et reçus.',
      points: [
        { title: 'Octroyer & fixer le markup', desc: 'Offrez un SIGN à un partenaire avec une dérogation de markup optionnelle et une fenêtre de validité.' },
        { title: 'Accepter / refuser', desc: 'Le bénéficiaire accepte pour commencer à émettre sur le SIGN, ou refuse.' },
        { title: 'Alimente les règles Wholesale', desc: 'Le markup de l’octroi est le repli hérité ; les règles Wholesale explicites de Markup & Rules (portée AGENCY / SIGN) le remplacent.' },
      ],
    },
    related: ['signs', 'partner-access', 'markup', 'agencies'],
  },

  signs: {
    title: 'SIGNs',
    tag: 'Identifiants d’émission',
    tagline: 'Les identifiants BSP / IATA / CASS qu’une agence utilise pour émettre des billets.',
    business: {
      lead: 'Un SIGN est l’identifiant enregistré qui permet à une agence d’émettre des billets auprès d’un fournisseur précis. C’est l’actif au cœur de la couche Wholesale : le propriétaire du SIGN peut émettre directement, ou prêter le SIGN à des partenaires (accès SIGN) et marger sur ce qu’ils émettent.',
      points: [
        { title: 'Le droit de billetter', desc: 'Sans un SIGN — ou un accès emprunté à un SIGN — une agence peut vendre mais pas émettre auprès de ce fournisseur.' },
        { title: 'Un seul actif par fournisseur', desc: 'Une agence détient un SIGN actif par fournisseur ; ceux remplacés sont retirés, pas supprimés.' },
        { title: 'L’actif wholesale', desc: 'Prêter un SIGN et marger sur l’émission du bénéficiaire est l’affaire de la couche Wholesale.' },
      ],
    },
    software: {
      lead: 'Le registre des SIGNs d’une agence, avec leur fournisseur, code, émetteur et validité.',
      points: [
        { title: 'Enregistrer un SIGN', desc: 'Consignez le fournisseur, le code SIGN, l’émetteur et le compte BSP avec une fenêtre de validité.' },
        { title: 'Détenu par une agence', desc: 'Les SIGNs sont toujours détenus par une agence (les entreprises détiennent des canaux, pas des SIGNs).' },
        { title: 'Partager via l’accès SIGN', desc: 'Prêtez un SIGN à un partenaire depuis Accès SIGN ; révoquez lorsque la relation se termine.' },
      ],
    },
    related: ['sign-access', 'partner-access', 'markup', 'agencies'],
  },

  branches: {
    title: 'Succursales',
    tag: 'Structure org.',
    tagline: 'Les sous-bureaux d’une agence, utilisés pour cadrer le personnel et les réservations.',
    business: {
      lead: 'Les succursales modélisent les sous-bureaux physiques ou organisationnels d’une agence. Elles permettent à une agence plus grande d’organiser ses collaborateurs et d’attribuer les réservations et la performance au bureau qui les a réalisées, sans se scinder en agences distinctes.',
      points: [
        { title: 'Attribution', desc: 'Les réservations et les revenus peuvent être rapportés par succursale, pour voir quel bureau performe.' },
        { title: 'Cadrage du personnel', desc: 'Les utilisateurs sont affectés à une succursale, limitant leur vue et leurs permissions à leur bureau.' },
        { title: 'Une agence, plusieurs bureaux', desc: 'Les succursales maintiennent une agence multi-bureaux comme une seule relation commerciale.' },
      ],
    },
    software: {
      lead: 'Le répertoire des succursales d’une agence.',
      points: [
        { title: 'Créer & modifier', desc: 'Ajoutez des succursales avec leur nom, contact et coordonnées de localisation.' },
        { title: 'Affecter le personnel', desc: 'La succursale est utilisée lors du provisionnement des utilisateurs et des rôles sous l’agence.' },
        { title: 'Vue admin vs agence', desc: 'Un admin gère les succursales de toute agence ; une agence gère les siennes.' },
      ],
    },
    related: ['agencies', 'agency-profile'],
  },

  'agency-groups': {
    title: 'Groupes d’agences',
    tag: 'Segmentation',
    tagline: 'Des lots nommés d’agences que vous pouvez cibler avec une seule règle de tarification.',
    business: {
      lead: 'Les groupes d’agences sont des lots de segmentation — « Spécialistes Omra », « Entreprises de Téhéran » — qui vous permettent de tarifer un ensemble d’agences ensemble plutôt qu’une par une. Ils alimentent la portée partenaire GROUP dans le moteur de tarification.',
      points: [
        { title: 'Tarifer par segment', desc: 'Un groupe est une cible pour une règle Markup & Rules avec portée partenaire = Group.' },
        { title: 'L’appartenance est plusieurs-à-plusieurs', desc: 'Une agence peut appartenir à plusieurs groupes ; un groupe contient plusieurs agences.' },
        { title: 'Cadré par propriétaire', desc: 'Un groupe est partagé sur la plateforme ou détenu par une agence, et suit un cycle de vie de statut (jamais supprimé définitivement).' },
      ],
    },
    software: {
      lead: 'La surface de gestion des groupes et de leurs appartenances.',
      points: [
        { title: 'Créer & curer', desc: 'Définissez un groupe et ajoutez ou retirez des agences membres.' },
        { title: 'Cibler dans la tarification', desc: 'Le groupe apparaît dans le sélecteur de portée partenaire Group de l’éditeur de règles.' },
        { title: 'Cycle de vie, pas suppression', desc: 'Retirez un groupe par statut afin que les règles existantes qui le référencent restent résolvables.' },
      ],
    },
    related: ['markup', 'agencies'],
  },
};
